'use client'

import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { useRouter } from 'next/navigation'
import EditAccount from '@/components/otherPages/EditAccount'
import { loginWithCredentials } from '@/utils/auth'
import { useContextElement } from '@/context/Context'

const API_REQUEST_OTP = '/api/member/register/register_requestOtp'
const API_VERIFY_OTP = '/api/member/register/register_verifyOtp'
const API_ADD_MEMBER = '/api/member/register/add_Member'
const API_GET_OFFLINE_STORES = '/api/offline/get_OfflineStoreList'
const API_GET_BY_MOBILE = '/api/member/get_MemberbyMobliePhone'
const API_ADD_POINTS = '/api/member/add_RegisteredMemberPoints'
const API_GET_SIGNUP_POINTS = '/api/member/get_SignupBonusPoints'
const API_GET_STORE_INFO = '/api/online/get_StoreInfo'
const API_GET_PARTNER_STORES = '/api/member/get_PartnerStoreList'
const API_GET_STORE_REFERRERS = '/api/member/get_StoreReferrers'

const defaultBirthday = '2000-01-01'
const OTP_LENGTH = 6

const pad2 = (value) => String(value).padStart(2, '0')
const daysInMonth = (year, month) => new Date(year, month, 0).getDate()
const toFlag = (value) => value === true || value === 1 || value === '1'
const unwrapData = (payload) => payload?.Data ?? payload ?? {}
const normalizeOfflineStoreId = (value) => {
  const text = String(value ?? '').trim()
  if (!text || text === "'") return ''
  return text
}

const normalizeStoreCode = (value) =>
  String(value ?? '')
    .trim()
    .toUpperCase()

const parseStoreCodeFromQr = (value) => {
  const raw = String(value ?? '').trim()
  if (!raw) return ''

  try {
    const url = new URL(raw)
    const candidateKeys = [
      'storeCode',
      'StoreCode',
      'code',
      'Code',
      'store',
      'Store',
      'id',
      'ID',
      'storeId',
      'StoreID',
      'PartnerStoreID',
      'OfflineStoreID',
    ]

    for (const key of candidateKeys) {
      const hit = String(url.searchParams.get(key) || '').trim()
      if (hit) return hit
    }

    const lastSegment = url.pathname
      .split('/')
      .map((part) => part.trim())
      .filter(Boolean)
      .pop()

    return lastSegment || raw
  } catch {
    return raw
  }
}

const isValidPassword = (pw) =>
  typeof pw === 'string' &&
  pw.length >= 8 &&
  pw.length <= 20 &&
  /[A-Za-z]/.test(pw) &&
  /\d/.test(pw)

const isValidOptionalEmail = (value) => {
  const text = String(value || '').trim()
  if (!text) return true
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)
}

const isValidTaiwanMobile = (value) =>
  /^09\d{8}$/.test(String(value || '').trim())

const isValidMemberName = (value) => {
  const text = String(value || '').trim()
  if (!text) return false
  return /^[\p{L}\s]+$/u.test(text)
}

const normalizeOtp = (value) =>
  String(value ?? '')
    .replace(/\D/g, '')
    .slice(0, OTP_LENGTH)

const formatReferrerLabel = (item) => {
  const empId = String(item?.EmpID ?? '').trim()
  const empName = String(item?.EmpName ?? '').trim()

  if (!empId && !empName) return ''
  if (!empName || empName === empId) return empId
  return `${empName} (${empId})`
}

// GTM / GA4
const pushDataLayerEvent = (eventName, ecommerce = null, extra = {}) => {
  if (typeof window === 'undefined') return null // 改為回傳 null

  window.dataLayer = window.dataLayer || []

  if (ecommerce) {
    window.dataLayer.push({ ecommerce: null })
  }

  // 1. 先把要推入的完整物件組合起來
  const finalPayload = {
    event: eventName,
    ...(ecommerce ? { ecommerce } : {}),
    ...extra,
  }

  // 2. 推入 dataLayer
  window.dataLayer.push(finalPayload)

  // 3. 回傳這個完整物件，讓外部可以使用
  return finalPayload
}

export function RegisterWizard({
  defaultMobile = '',
  lineId = '',
  fromWeb = false,
  redirectTo = '',
}) {
  const router = useRouter()
  const { writeMarketingEventLog } = useContextElement()
  const hasLineId = !!String(lineId || '').trim()
  const isLineMode = hasLineId && !fromWeb
  const canUseLineQrScanner = hasLineId
  const lineLiffId = String(process.env.NEXT_PUBLIC_LIFF_ID || '').trim()
  const isMobileLocked = !!String(defaultMobile || '').trim()
  const storeNumber = String(process.env.NEXT_PUBLIC_STORE_NUMBER || '').trim()
  const isChiachiRegistration = storeNumber === '14'

  const [step, setStep] = useState(1)
  const [mobile, setMobile] = useState(defaultMobile || '')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [birthday, setBirthday] = useState(defaultBirthday)
  const [birthYear, setBirthYear] = useState(2000)
  const [birthMonth, setBirthMonth] = useState(1)
  const [birthDay, setBirthDay] = useState(1)
  const [memberName, setMemberName] = useState('')
  const [email, setEmail] = useState('')
  const [gender, setGender] = useState('')
  const [isEmailRequired, setIsEmailRequired] = useState(false)

  const [stores, setStores] = useState([])
  const [selectedStoreId, setSelectedStoreId] = useState('')
  const [loadingStores, setLoadingStores] = useState(false)

  const [hasFranchise, setHasFranchise] = useState(false)
  const [storeMode, setStoreMode] = useState('offline')
  const [defaultOfflineStoreId, setDefaultOfflineStoreId] = useState('')
  const [partnerStores, setPartnerStores] = useState([])
  const [selectedPartnerStoreId, setSelectedPartnerStoreId] = useState('')
  const [loadingPartnerStores, setLoadingPartnerStores] = useState(false)
  const [scannerOpen, setScannerOpen] = useState(false)
  const [scannerSupported, setScannerSupported] = useState(false)

  const [isReferralRequired, setIsReferralRequired] = useState(false)
  const [referrers, setReferrers] = useState([])
  const [selectedReferrerId, setSelectedReferrerId] = useState('')
  const [loadingReferrers, setLoadingReferrers] = useState(false)

  const [sessionId, setSessionId] = useState(null)
  const [otp, setOtp] = useState('')
  const [cooldown, setCooldown] = useState(0)
  const [loading, setLoading] = useState(false)

  // Google ADS
  const [adsId, setAdsId] = useState(null)
  const [labelMemberSuccess, setLabelMemberSuccess] = useState(null)

  const qrScannerRef = useRef(null)
  const qrScannerContainerRef = useRef(null)
  const liffReadyRef = useRef(false)

  useEffect(
    () => () => {
      void stopQrScanner()
    },
    []
  )

  const isAbortError = (error) =>
    error?.name === 'AbortError' ||
    String(error?.message || '')
      .toLowerCase()
      .includes('aborted')

  const getJson = async (url, options = {}) => {
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
      ...(options.signal ? { signal: options.signal } : {}),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || data?.Error) {
      throw new Error(data?.Error || data?.error || `HTTP ${response.status}`)
    }
    return data
  }

  const postJson = async (url, body, options = {}) => {
    const response = await fetch(url, {
      method: 'POST',
      cache: 'no-store',
      ...(options.signal ? { signal: options.signal } : {}),
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || data?.Error) {
      throw new Error(data?.Error || data?.error || `HTTP ${response.status}`)
    }
    return data
  }

  const formatOccDate8 = (date = new Date()) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}${month}${day}`
  }

  useEffect(() => {
    setScannerSupported(
      typeof window !== 'undefined' && !!navigator?.mediaDevices?.getUserMedia
    )
  }, [])

  useEffect(() => {
    const [year, month, day] = (birthday || defaultBirthday)
      .split('-')
      .map(Number)

    setBirthYear(year || 2000)
    setBirthMonth(month || 1)
    setBirthDay(day || 1)
  }, [birthday])

  useEffect(() => {
    if (!birthYear || !birthMonth || !birthDay) return

    const maxDay = daysInMonth(birthYear, birthMonth)
    const safeDay = Math.min(birthDay, maxDay)

    if (safeDay !== birthDay) {
      setBirthDay(safeDay)
      return
    }

    setBirthday(`${birthYear}-${pad2(birthMonth)}-${pad2(safeDay)}`)
  }, [birthYear, birthMonth, birthDay])

  useEffect(() => {
    if (!hasFranchise) {
      setStoreMode('offline')
    }
  }, [hasFranchise])

  useEffect(() => {
    const controller = new AbortController()

    const fetchPartnerStores = async () => {
      if (!isLineMode || !hasFranchise || !storeNumber) {
        setPartnerStores([])
        setSelectedPartnerStoreId('')
        setLoadingPartnerStores(false)
        return
      }

      setLoadingPartnerStores(true)

      try {
        const query = new URLSearchParams({ StoreNumber: storeNumber })
        const payload = await getJson(
          `${API_GET_PARTNER_STORES}?${query.toString()}`,
          { signal: controller.signal }
        )
        const rawList = payload?.Data
        const list = Array.isArray(rawList) ? rawList : []
        const shouldForceQrStoreSelection =
          storeNumber === '8' && canUseLineQrScanner && hasFranchise

        setPartnerStores(list)
        setSelectedPartnerStoreId((current) => {
          const exists =
            current &&
            list.some((item) => String(item?.PartnerStoreID ?? '') === current)
          if (exists) return current
          return shouldForceQrStoreSelection
            ? ''
            : String(list[0]?.PartnerStoreID ?? '')
        })
      } catch (error) {
        if (isAbortError(error)) return
        console.warn('fetch partner stores failed:', error)
        setPartnerStores([])
        setSelectedPartnerStoreId('')
        toast.error('無法取得加盟店資料，請稍後再試')
      } finally {
        setLoadingPartnerStores(false)
      }
    }

    fetchPartnerStores()

    return () => controller.abort()
  }, [hasFranchise, isLineMode, storeNumber])

  useEffect(() => {
    const controller = new AbortController()

    const fetchStoreSetup = async () => {
      setLoadingStores(true)

      try {
        let resolvedDefaultOfflineStoreId = ''
        let resolvedHasFranchise = false

        if (storeNumber) {
          try {
            const query = new URLSearchParams({ StoreNumber: storeNumber })
            const storeInfoResponse = await getJson(
              `${API_GET_STORE_INFO}?${query.toString()}`,
              { signal: controller.signal }
            )
            const storeInfo = unwrapData(storeInfoResponse)

            setAdsId(storeInfo?.GoogleAdsID)
            setLabelMemberSuccess(storeInfo?.LabelMemberSuccess)

            resolvedHasFranchise = toFlag(storeInfo?.HasFranchise)
            setHasFranchise(resolvedHasFranchise)
            setIsReferralRequired(toFlag(storeInfo?.IsReferralRequired))
            setIsEmailRequired(toFlag(storeInfo?.IsEmailRequired))
            resolvedDefaultOfflineStoreId = normalizeOfflineStoreId(
              storeInfo?.DefaultOfflineStoreID
            )
            setDefaultOfflineStoreId(resolvedDefaultOfflineStoreId)
          } catch (error) {
            if (isAbortError(error)) return
            console.warn('fetch store info failed:', error)
            setHasFranchise(false)
            setIsReferralRequired(false)
            setIsEmailRequired(false)
            setDefaultOfflineStoreId('')
          }
        }

        const url = `${API_GET_OFFLINE_STORES}?StoreNumber=${encodeURIComponent(
          storeNumber
        )}`
        const response = await fetch(url, {
          cache: 'no-store',
          signal: controller.signal,
        })
        const data = await response.json().catch(() => ({}))
        const list = data?.Data?.StoreData || []
        const shouldForceQrStoreSelection =
          storeNumber === '8' && canUseLineQrScanner && resolvedHasFranchise

        setStores(list)

        if (shouldForceQrStoreSelection) {
          setSelectedStoreId((current) => {
            const exists =
              current &&
              list.some(
                (item) => String(item?.OfflineStoreID ?? '') === String(current)
              )
            return exists ? current : ''
          })
        } else if (list.length === 1 && !isChiachiRegistration) {
          setSelectedStoreId(String(list[0]?.OfflineStoreID ?? ''))
        } else if (list.length > 0) {
          setSelectedStoreId((current) => {
            const exists =
              current &&
              list.some(
                (item) => String(item?.OfflineStoreID ?? '') === String(current)
              )
            return exists ? current : ''
          })
        } else {
          setSelectedStoreId('')
        }
      } catch (error) {
        if (isAbortError(error)) return
        console.warn('fetch stores failed:', error)
        toast.error('無法取得註冊店櫃，請稍後再試')
      } finally {
        setLoadingStores(false)
      }
    }

    fetchStoreSetup()

    return () => controller.abort()
  }, [isChiachiRegistration, isLineMode, storeNumber])

  const isQrStoreSelectionEnabled =
    storeNumber === '8' && canUseLineQrScanner && hasFranchise
  const usesStoreAndReferrerSelection = isLineMode || isChiachiRegistration
  const usingFranchise =
    !isChiachiRegistration && hasFranchise && storeMode === 'franchise'
  const shouldShowStoreModeChooser =
    hasFranchise && isLineMode && !isQrStoreSelectionEnabled
  const shouldShowOfflineStoreSelect =
    usesStoreAndReferrerSelection &&
    !usingFranchise &&
    !isQrStoreSelectionEnabled
  const shouldShowPartnerStoreSelect =
    isLineMode && usingFranchise && !isQrStoreSelectionEnabled
  const shouldRequireRegistrationStore = isQrStoreSelectionEnabled
    ? true
    : !usingFranchise &&
      (usesStoreAndReferrerSelection || !!defaultOfflineStoreId)
  const effectiveStoreId = usingFranchise
    ? ''
    : isQrStoreSelectionEnabled
      ? selectedStoreId || ''
      : selectedStoreId || defaultOfflineStoreId || ''
  const effectivePartnerStoreId = usingFranchise ? selectedPartnerStoreId : ''
  const hasSelectedRegistrationStore = isQrStoreSelectionEnabled
    ? usingFranchise
      ? !!effectivePartnerStoreId
      : !!selectedStoreId
    : usingFranchise
      ? !!effectivePartnerStoreId
      : !shouldRequireRegistrationStore || !!effectiveStoreId
  const shouldShowReferrer =
    usesStoreAndReferrerSelection &&
    !usingFranchise &&
    (isReferralRequired || isChiachiRegistration) &&
    !!selectedStoreId
  const selectedOfflineStore = stores.find(
    (item) => String(item?.OfflineStoreID ?? '') === String(selectedStoreId)
  )
  const selectedPartnerStore = partnerStores.find(
    (item) =>
      String(item?.PartnerStoreID ?? '') === String(selectedPartnerStoreId)
  )
  const scannedStoreSummary = usingFranchise
    ? selectedPartnerStore
      ? `加盟店：${selectedPartnerStore.PartnerStoreName} (${selectedPartnerStore.PartnerStoreID})`
      : ''
    : selectedOfflineStore
      ? `註冊店櫃：${selectedOfflineStore.OfflineStoreName} (${selectedOfflineStore.OfflineStoreID})`
      : ''

  useEffect(() => {
    const controller = new AbortController()

    const fetchReferrers = async () => {
      if (!shouldShowReferrer) {
        setReferrers([])
        setSelectedReferrerId('')
        setLoadingReferrers(false)
        return
      }

      setLoadingReferrers(true)

      try {
        const query = new URLSearchParams({
          StoreNumber: storeNumber,
          OfflineStoreID: selectedStoreId,
        })
        const payload = await getJson(
          `${API_GET_STORE_REFERRERS}?${query.toString()}`,
          { signal: controller.signal }
        )
        const rawList = payload?.Data
        const list = Array.isArray(rawList) ? rawList : []

        setReferrers(list)
        setSelectedReferrerId(
          list.length > 0 ? String(list[0]?.EmpID ?? '') : ''
        )
      } catch (error) {
        if (isAbortError(error)) return
        console.warn('fetch referrers failed:', error)
        setReferrers([])
        setSelectedReferrerId('')
        toast.error('無法取得推薦人資料，請稍後再試')
      } finally {
        setLoadingReferrers(false)
      }
    }

    fetchReferrers()

    return () => controller.abort()
  }, [selectedStoreId, shouldShowReferrer, storeNumber])

  useEffect(() => {
    if (cooldown <= 0) return

    const timer = setInterval(() => {
      setCooldown((current) => (current > 0 ? current - 1 : 0))
    }, 1000)

    return () => clearInterval(timer)
  }, [cooldown])

  const stopQrScanner = async () => {
    const scanner = qrScannerRef.current
    qrScannerRef.current = null

    if (!scanner) return

    try {
      await scanner.stop()
    } catch (error) {
      console.warn('stop qr scanner failed:', error)
    }

    try {
      await scanner.clear()
    } catch (error) {
      console.warn('clear qr scanner failed:', error)
    }
  }

  const applyScannedStoreCode = (rawValue) => {
    const parsedCode = parseStoreCodeFromQr(rawValue)
    const normalizedCode = normalizeStoreCode(parsedCode)

    if (!normalizedCode) {
      toast.error('QR Code 內容無法辨識店櫃代號')
      return false
    }

    if (normalizedCode.startsWith('Z')) {
      const matchedPartnerStore = partnerStores.find(
        (item) =>
          normalizeStoreCode(item?.PartnerCode || item?.PartnerStoreID) ===
            normalizedCode ||
          normalizeStoreCode(item?.PartnerStoreID) === normalizedCode
      )

      if (!matchedPartnerStore) {
        toast.error(`查無加盟店代號：${parsedCode}`)
        return false
      }

      setStoreMode('franchise')
      setSelectedPartnerStoreId(String(matchedPartnerStore.PartnerStoreID))
      setSelectedStoreId('')
      setReferrers([])
      setSelectedReferrerId('')
      toast.success(`已帶入加盟店：${matchedPartnerStore.PartnerStoreName}`)
      return true
    }

    const matchedOfflineStore = stores.find(
      (item) => normalizeStoreCode(item?.OfflineStoreID) === normalizedCode
    )

    if (!matchedOfflineStore) {
      toast.error(`查無註冊店櫃代號：${parsedCode}`)
      return false
    }

    setStoreMode('offline')
    setSelectedPartnerStoreId('')
    setSelectedStoreId(String(matchedOfflineStore.OfflineStoreID))
    toast.success(`已帶入註冊店櫃：${matchedOfflineStore.OfflineStoreName}`)
    return true
  }

  const tryScanQrCodeWithLiff = async () => {
    if (!canUseLineQrScanner || !lineLiffId || typeof window === 'undefined') {
      return {
        available: false,
        value: '',
        reason: !canUseLineQrScanner
          ? 'not-line-mode'
          : !lineLiffId
            ? 'missing-liff-id'
            : 'no-window',
      }
    }

    try {
      const liffModule = await import('@line/liff')
      const liff = liffModule?.default || liffModule

      if (!liffReadyRef.current) {
        await liff.init({
          liffId: lineLiffId,
          withLoginOnExternalBrowser: false,
        })
        liffReadyRef.current = true
      }

      if (typeof liff.isLoggedIn === 'function' && !liff.isLoggedIn()) {
        return { available: false, value: '', reason: 'liff-not-logged-in' }
      }

      if (
        typeof liff.isApiAvailable !== 'function' ||
        !liff.isApiAvailable('scanCodeV2') ||
        typeof liff.scanCodeV2 !== 'function'
      ) {
        return {
          available: false,
          value: '',
          reason: 'scanCodeV2-unavailable',
        }
      }

      const result = await liff.scanCodeV2()
      return {
        available: true,
        value: String(result?.value || '').trim(),
        reason: 'scan-success',
      }
    } catch (error) {
      console.warn('liff scanCodeV2 failed:', error)
      return {
        available: false,
        value: '',
        reason: 'liff-init-or-scan-failed',
        error,
      }
    }
  }

  useEffect(() => {
    if (!scannerOpen) {
      void stopQrScanner()
      return
    }

    let cancelled = false

    const startScanner = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode')

        if (cancelled || !qrScannerContainerRef.current) {
          return
        }

        const scanner = new Html5Qrcode('register-qr-reader', false)
        qrScannerRef.current = scanner

        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 220, height: 220 },
            aspectRatio: 1,
          },
          async (decodedText) => {
            if (cancelled) return
            if (!applyScannedStoreCode(decodedText)) return

            cancelled = true
            setScannerOpen(false)
            await stopQrScanner()
          },
          () => {}
        )
      } catch (error) {
        console.warn('start qr scanner failed:', error)
        toast.error('無法啟用相機掃描 QR Code，請確認相機權限')
        await stopQrScanner()
        setScannerOpen(false)
      }
    }

    void startScanner()

    return () => {
      cancelled = true
      void stopQrScanner()
    }
  }, [scannerOpen, partnerStores, stores])

  const handleOpenQrScanner = async () => {
    if (loadingStores || loadingPartnerStores) {
      toast.error('店櫃資料讀取中，請稍後再試')
      return
    }

    const liffScanResult = await tryScanQrCodeWithLiff()

    if (liffScanResult.available) {
      if (liffScanResult.value) {
        applyScannedStoreCode(liffScanResult.value)
      } else {
        toast.error('未取得 QR Code 內容，請再試一次')
      }
      return
    }

    if (canUseLineQrScanner && liffScanResult.reason !== 'not-line-mode') {
      const reasonMessageMap = {
        'missing-liff-id': 'LINE 掃碼設定未完成，缺少 LIFF ID',
        'liff-not-logged-in': '請從 LINE 指定入口進入後再掃碼',
        'scanCodeV2-unavailable': '此 LINE LIFF App 尚未開啟 Scan QR 功能',
        'liff-init-or-scan-failed':
          'LINE 掃碼功能初始化失敗，請確認 LIFF 設定或入口網址',
      }

      toast.error(
        reasonMessageMap[liffScanResult.reason] || 'LINE 掃碼目前不可用'
      )
      return
    }

    if (!scannerSupported) {
      toast.error('此裝置目前不支援 QR Code 掃描')
      return
    }

    setScannerOpen(true)
  }

  const handleRequestOtp = async () => {
    if (!mobile.trim()) {
      toast.error('請輸入手機')
      return
    }

    if (!isValidTaiwanMobile(mobile)) {
      toast.error('電話號碼格式錯誤')
      return
    }

    if (!memberName.trim()) {
      toast.error('請輸入姓名')
      return
    }

    if (!isValidMemberName(memberName)) {
      toast.error('姓名不可包含特殊符號')
      return
    }

    if (!gender) {
      toast.error('請選擇性別')
      return
    }

    if (isEmailRequired && !email.trim()) {
      toast.error('請輸入電子信箱')
      return
    }

    if (!isValidOptionalEmail(email)) {
      toast.error('請輸入正確的電子信箱格式')
      return
    }

    if (!hasSelectedRegistrationStore) {
      toast.error(
        isQrStoreSelectionEnabled
          ? '請先掃描店櫃 QR Code'
          : usingFranchise
            ? '請選擇加盟店'
            : '請選擇註冊店櫃'
      )
      return
    }

    if (shouldShowReferrer && !selectedReferrerId) {
      toast.error('請選擇推薦人')
      return
    }

    if (!isLineMode) {
      if (!password || !confirmPassword) {
        toast.error('請輸入密碼與再次輸入密碼')
        return
      }

      if (password !== confirmPassword) {
        toast.error('兩次輸入的密碼不一致')
        return
      }

      if (!isValidPassword(password)) {
        toast.error('密碼需為 8 到 20 碼，且同時包含英文與數字')
        return
      }
    }

    setLoading(true)

    try {
      const data = await postJson(API_REQUEST_OTP, {
        StoreNumber: process.env.NEXT_PUBLIC_STORE_NUMBER,
        Channel: 'sms',
        Mobile: mobile.trim(),
        LineID: hasLineId ? lineId : null,
      })

      setSessionId(data.SessionID)
      setStep(2)
      setCooldown(60)
      toast.success('驗證碼已送出')
    } catch (error) {
      toast.error(`取得驗證碼失敗：${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    if (cooldown > 0) return
    await handleRequestOtp()
  }

  const handleVerifyAndCreate = async () => {
    if (!sessionId) {
      toast.error('請先取得驗證碼')
      return
    }

    const normalizedOtp = normalizeOtp(otp)
    if (normalizedOtp.length !== OTP_LENGTH) {
      toast.error('請輸入 6 位數驗證碼')
      return
    }

    if (!isValidMemberName(memberName)) {
      toast.error('姓名不可包含特殊符號')
      return
    }

    setLoading(true)

    try {
      const verify = await postJson(API_VERIFY_OTP, {
        StoreNumber: process.env.NEXT_PUBLIC_STORE_NUMBER,
        SessionID: sessionId,
        Code: normalizedOtp,
      })

      const registrationToken = verify.RegistrationToken
      const passwordToSend = isLineMode ? mobile.trim() : password

      await postJson(API_ADD_MEMBER, {
        StoreNumber: Number(process.env.NEXT_PUBLIC_STORE_NUMBER),
        RegistrationToken: registrationToken,
        UserName: mobile.trim(),
        Password: passwordToSend,
        Mobile: mobile.trim(),
        Mail01: email.trim() || null,
        Gender: Number(gender),
        Birthday: birthday,
        LineID: hasLineId ? lineId : null,
        MemberName: memberName.trim(),
        OfflineStoreID: usingFranchise ? '' : effectiveStoreId || null,
        ReferrerID: shouldShowReferrer ? selectedReferrerId || null : null,
        PartnerStoreID: usingFranchise ? effectivePartnerStoreId || null : null,
      })

      // GTM / GA4 + Google Ads：會員註冊成功後送出追蹤
      if (typeof window !== 'undefined') {
        // =========================
        // GTM / GA4
        // =========================
        const ga4Data = {
          method: isLineMode ? 'line' : 'phone',
        }

        // 執行並拿到最終產出的完整 payload 物件
        const finalLogData = pushDataLayerEvent('sign_up', ga4Data)

        // 如果是在客戶端（非 SSR 階段）且有成功拿到資料，就寫入 Log
        if (finalLogData) {
          writeMarketingEventLog('GA4', 'sign_up', finalLogData)
        }

        // =========================
        // Google Ads
        // =========================
        if (adsId && labelMemberSuccess) {
          const googleAdsData = {
            send_to: `AW-${adsId}/${labelMemberSuccess}`,
          }

          window.gtag?.('event', 'conversion', googleAdsData)

          writeMarketingEventLog('GoogleAds', 'conversion', googleAdsData)
        }
      }

      try {
        await loginWithCredentials({
          account: mobile.trim(),
          password: passwordToSend,
          rememberMe: true,
          onSuccess: () => toast.success('註冊成功，已為您自動登入'),
          onError: () => toast.error('自動登入失敗，請稍後手動登入'),
        })
      } catch {}

      // try {
      //   if (isLineMode) {
      //     const memberQuery = new URLSearchParams({
      //       StoreNumber: String(process.env.NEXT_PUBLIC_STORE_NUMBER),
      //       Mobile: mobile.trim(),
      //     })
      //     const memberInfo = await getJson(
      //       `${API_GET_BY_MOBILE}?${memberQuery.toString()}`
      //     )
      //     const memberId = memberInfo?.MemberID ?? memberInfo?.Data?.MemberID

      //     if (memberId) {
      //       let signupPoints = 0

      //       try {
      //         const pointsQuery = new URLSearchParams({
      //           StoreNumber: String(process.env.NEXT_PUBLIC_STORE_NUMBER),
      //         })
      //         const signupInfo = await getJson(
      //           `${API_GET_SIGNUP_POINTS}?${pointsQuery.toString()}`
      //         )
      //         const rawPoints =
      //           signupInfo?.SignupBonusPoints ??
      //           signupInfo?.Data?.SignupBonusPoints ??
      //           signupInfo?.points?.SignupBonusPoints
      //         const parsedPoints = Number(rawPoints)

      //         if (Number.isFinite(parsedPoints) && parsedPoints > 0) {
      //           signupPoints = parsedPoints
      //         }
      //       } catch {}

      //       await postJson(API_ADD_POINTS, {
      //         MemberID: Number(memberId),
      //         OccDate: formatOccDate8(),
      //         Point: signupPoints,
      //       })
      //     }
      //   }
      // } catch {}

      setStep(3)

      if (redirectTo) {
        setTimeout(() => {
          window.location.href = redirectTo
        }, 600)
      }
    } catch (error) {
      toast.error(`註冊失敗：${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const currentYear = new Date().getFullYear()
  const years = Array.from(
    { length: currentYear - 1900 + 1 },
    (_, index) => 1900 + index
  )
  const months = Array.from({ length: 12 }, (_, index) => index + 1)
  const days = Array.from(
    { length: daysInMonth(birthYear, birthMonth) },
    (_, index) => index + 1
  )

  const isStepOneDisabled =
    loading ||
    !mobile.trim() ||
    !isValidTaiwanMobile(mobile) ||
    !memberName.trim() ||
    !gender ||
    (isEmailRequired && !email.trim()) ||
    !hasSelectedRegistrationStore ||
    (usingFranchise && (loadingPartnerStores || !selectedPartnerStoreId)) ||
    (shouldShowReferrer && (loadingReferrers || !selectedReferrerId)) ||
    !isValidOptionalEmail(email) ||
    (!isLineMode &&
      (!password ||
        !confirmPassword ||
        password !== confirmPassword ||
        !isValidPassword(password)))

  return (
    <div className="register-form">
      {step === 1 && (
        <form
          onSubmit={(event) => event.preventDefault()}
          className="needs-validation"
        >
          <div className="form-floating mb-3">
            <input
              type="tel"
              className="form-control form-control_gray"
              style={{ fontSize: '16px' }}
              placeholder="手機 *"
              value={mobile}
              onChange={(event) => {
                if (!isMobileLocked) setMobile(event.target.value.trim())
              }}
              readOnly={isMobileLocked}
              required
            />
            <label>手機 *</label>
          </div>

          {mobile && !isValidTaiwanMobile(mobile) && (
            <small style={{ color: 'red' }}>電話號碼格式錯誤</small>
          )}

          <div className="form-floating mb-3">
            <input
              type="text"
              className="form-control form-control_gray"
              style={{ fontSize: '16px' }}
              placeholder="姓名 *"
              value={memberName}
              onChange={(event) => setMemberName(event.target.value)}
              required
            />
            <label>姓名 *</label>
          </div>

          {isEmailRequired && (
            <div className="form-floating mb-2">
              <input
                type="email"
                className="form-control form-control_gray"
                style={{ fontSize: '16px' }}
                placeholder="Email *"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
              <label>Email *</label>
            </div>
          )}

          {isEmailRequired && email && !isValidOptionalEmail(email) && (
            <small style={{ color: 'red' }}>請輸入正確的電子信箱格式</small>
          )}

          {!isLineMode && (
            <>
              <div className="form-floating mb-3">
                <input
                  type="password"
                  className="form-control form-control_gray"
                  style={{ fontSize: '16px' }}
                  placeholder="密碼 *"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  minLength={8}
                  maxLength={20}
                  required
                />
                <label>密碼 *</label>
              </div>

              <div className="form-floating mb-2">
                <input
                  type="password"
                  className="form-control form-control_gray"
                  style={{ fontSize: '16px' }}
                  placeholder="再次輸入密碼 *"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  minLength={8}
                  maxLength={20}
                  required
                />
                <label>再次輸入密碼 *</label>
              </div>

              {confirmPassword && confirmPassword !== password && (
                <small style={{ color: 'red' }}>兩次輸入的密碼不一致</small>
              )}
              {password && !isValidPassword(password) && (
                <small style={{ color: 'red' }}>
                  密碼需為 8 到 20 碼，且同時包含英文與數字
                </small>
              )}
            </>
          )}

          <div className="pb-3" />

          <div className="mb-3">
            <div className="d-flex gap-2">
              <div className="form-floating w-50">
                <select
                  id="birthYear"
                  className="form-select form-select_gray"
                  value={birthYear}
                  onChange={(event) => setBirthYear(Number(event.target.value))}
                  required
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year} 年
                    </option>
                  ))}
                </select>
                <label htmlFor="birthYear">生日 *</label>
              </div>

              <select
                className="form-select form-select_gray w-25"
                value={birthMonth}
                onChange={(event) => setBirthMonth(Number(event.target.value))}
                required
              >
                {months.map((month) => (
                  <option key={month} value={month}>
                    {month} 月
                  </option>
                ))}
              </select>

              <select
                className="form-select form-select_gray w-25"
                value={birthDay}
                onChange={(event) => setBirthDay(Number(event.target.value))}
                required
              >
                {days.map((day) => (
                  <option key={day} value={day}>
                    {day} 日
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-floating mb-3">
            <select
              className="form-select form-select_gray"
              value={gender}
              onChange={(event) => setGender(event.target.value)}
              required
            >
              <option value="">請選擇性別</option>
              <option value="1">男性</option>
              <option value="2">女性</option>
              <option value="3">不方便透露</option>
            </select>
            <label>性別 *</label>
          </div>

          {shouldShowStoreModeChooser && (
            <div className="mb-3">
              <label className="form-label d-block">註冊方式 *</label>
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="storeMode"
                    id="storeModeOffline"
                    checked={storeMode === 'offline'}
                    onChange={() => setStoreMode('offline')}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="storeModeOffline"
                  >
                    註冊店櫃
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="storeMode"
                    id="storeModeFranchise"
                    checked={storeMode === 'franchise'}
                    onChange={() => setStoreMode('franchise')}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="storeModeFranchise"
                  >
                    加盟店
                  </label>
                </div>
              </div>
            </div>
          )}

          {isQrStoreSelectionEnabled && (
            <div className="mb-3">
              <label className="form-label d-block">店櫃 QR Code *</label>
              <button
                className="btn btn-outline-dark w-100 mb-2"
                type="button"
                onClick={handleOpenQrScanner}
                disabled={loadingStores || loadingPartnerStores}
              >
                掃描店櫃 QR Code
              </button>
              <div className="small text-muted">
                掃描後會自動判別為註冊店櫃或加盟店，避免選錯。
              </div>
              {scannedStoreSummary ? (
                <div className="mt-2 p-3 border rounded bg-light">
                  {scannedStoreSummary}
                </div>
              ) : null}
            </div>
          )}

          {shouldShowOfflineStoreSelect ? (
            <div className="form-floating mb-3">
              <select
                className="form-select form-select_gray"
                value={selectedStoreId}
                onChange={(event) => setSelectedStoreId(event.target.value)}
                disabled={loadingStores || stores.length === 0}
                required
              >
                {stores.length === 0 ? (
                  <option value="">
                    {loadingStores ? '讀取中...' : '目前沒有可用店櫃'}
                  </option>
                ) : (
                  <>
                    <option value="">請選擇註冊店櫃</option>
                    {stores.map((store) => (
                      <option
                        key={store.OfflineStoreID}
                        value={store.OfflineStoreID}
                      >
                        {store.OfflineStoreName}
                      </option>
                    ))}
                  </>
                )}
              </select>
              <label>註冊店櫃 *</label>
            </div>
          ) : null}
          {shouldShowPartnerStoreSelect ? (
            <div className="form-floating mb-3">
              <select
                className="form-select form-select_gray"
                value={selectedPartnerStoreId}
                onChange={(event) =>
                  setSelectedPartnerStoreId(event.target.value)
                }
                disabled={loadingPartnerStores || partnerStores.length === 0}
                required
              >
                {partnerStores.length === 0 ? (
                  <option value="">
                    {loadingPartnerStores ? '載入中...' : '目前沒有可選加盟店'}
                  </option>
                ) : (
                  partnerStores.map((store) => (
                    <option
                      key={String(store.PartnerStoreID)}
                      value={String(store.PartnerStoreID)}
                    >
                      {store.PartnerStoreName}
                    </option>
                  ))
                )}
              </select>
              <label>加盟店 *</label>
            </div>
          ) : null}

          {shouldShowReferrer && (
            <div className="form-floating mb-3">
              <select
                className="form-select form-select_gray"
                value={selectedReferrerId}
                onChange={(event) => setSelectedReferrerId(event.target.value)}
                disabled={loadingReferrers || referrers.length === 0}
                required
              >
                {referrers.length === 0 ? (
                  <option value="">
                    {loadingReferrers ? '讀取中...' : '查無推薦人'}
                  </option>
                ) : (
                  referrers.map((item) => (
                    <option key={String(item.EmpID)} value={String(item.EmpID)}>
                      {formatReferrerLabel(item)}
                    </option>
                  ))
                )}
              </select>
              <label>推薦人 *</label>
            </div>
          )}

          <button
            className="btn btn-primary w-100 text-uppercase"
            disabled={isStepOneDisabled}
            onClick={handleRequestOtp}
            type="button"
          >
            {loading ? '處理中...' : '取得驗證碼'}
          </button>
        </form>
      )}

      {step === 2 && (
        <form
          onSubmit={(event) => event.preventDefault()}
          className="needs-validation"
        >
          <div className="form-floating mb-3">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="form-control form-control_gray"
              placeholder="驗證碼 *"
              value={otp}
              onChange={(event) => setOtp(normalizeOtp(event.target.value))}
              maxLength={OTP_LENGTH}
              autoComplete="one-time-code"
              required
            />
            <label>驗證碼 *</label>
          </div>

          <div className="d-flex align-items-center mb-3 pb-2">
            <button
              className="btn btn-link p-0"
              type="button"
              disabled={cooldown > 0 || loading}
              onClick={handleResend}
            >
              重新發送{cooldown > 0 ? ` (${cooldown})` : ''}
            </button>
          </div>

          <button
            className="btn btn-primary w-100 text-uppercase"
            disabled={loading || otp.length !== OTP_LENGTH}
            onClick={handleVerifyAndCreate}
            type="button"
          >
            {loading ? '處理中...' : '完成註冊'}
          </button>
        </form>
      )}

      {scannerOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '420px',
              background: '#fff',
              borderRadius: '16px',
              padding: '16px',
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <strong>掃描店櫃 QR Code</strong>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setScannerOpen(false)}
              >
                關閉
              </button>
            </div>
            <div
              id="register-qr-reader"
              ref={qrScannerContainerRef}
              style={{
                width: '100%',
                borderRadius: '12px',
                backgroundColor: '#000',
                overflow: 'hidden',
              }}
            />
            <div className="small text-muted mt-2">
              請將店櫃 QR Code 對準畫面中央，系統會自動帶入店櫃。
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="text-center py-4">
          <h5 className="mb-3">註冊完成</h5>
          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={() => router.push('/')}
          >
            回到首頁
          </button>
          <p className="text-secondary mt-3">
            如果需要補充資料，也可以在這裡繼續完成。
          </p>
          <div className="d-flex justify-content-center gap-2 mt-2">
            <EditAccount onSaved={() => router.push('/')} />
          </div>
        </div>
      )}
    </div>
  )
}
